import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Heart, Building2, Crown, Check, Loader2, ArrowRight, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";

// Polling constants live at module scope so the polling effect has a stable
// dependency closure (no re-creation per render).
const POLL_INTERVAL_MS = 2200;
const POLL_MAX_ATTEMPTS = 7;

export default function Support() {
  const [params] = useSearchParams();
  const nav = useNavigate();

  // Initial tab can be deep-linked: /support?tab=license (used by footer
  // "Partner With Us" link). Falls back to donate.
  const initialTab = params.get("tab") === "license" ? "license" : "donate";
  const [tab, setTab] = useState(initialTab); // donate | license
  const [packages, setPackages] = useState(null);
  const [donateChoice, setDonateChoice] = useState("advocate");
  const [customAmount, setCustomAmount] = useState("");
  const [licenseChoice, setLicenseChoice] = useState("cohort");
  const [donor, setDonor] = useState({ donor_name: "", donor_email: "", organization: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [stripeMode, setStripeMode] = useState(null); // "live" | "test" | "unconfigured" | null
  const [checkoutError, setCheckoutError] = useState(""); // persistent inline error, not just a toast

  // Status polling state
  const [statusInfo, setStatusInfo] = useState(null);
  const sessionId = params.get("session_id");
  const cancelled = params.get("cancelled");

  useEffect(() => {
    api.get("/checkout/packages").then((r) => setPackages(r.data));
    // Fetch the actual Stripe mode so the "Test mode" banner reflects reality
    // instead of being hardcoded. Silently ignore errors — the banner just
    // won't render if this endpoint isn't available on an older deployment.
    api.get("/checkout/mode")
      .then((r) => setStripeMode(r.data?.mode || null))
      .catch(() => setStripeMode(null));
  }, []);

  // Poll on return from Stripe
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    let cancelledFlag = false;

    const poll = async () => {
      if (cancelledFlag) return;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setStatusInfo(data);
        if (data.payment_status === "paid") {
          toast.success("Payment successful — thank you for supporting TCPI.");
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          toast.error("Payment was not completed.");
          return;
        }
        if (attempts < POLL_MAX_ATTEMPTS) {
          attempts += 1;
          setTimeout(poll, POLL_INTERVAL_MS);
        } else {
          toast.message("Still processing — check your email for confirmation.");
        }
      } catch (err) {
        // Network blip — retry while attempts remain
        console.warn("Checkout status poll error (will retry):", err?.message);
        if (attempts < POLL_MAX_ATTEMPTS) {
          attempts += 1;
          setTimeout(poll, POLL_INTERVAL_MS);
        }
        // Otherwise: silently stop polling; the UI already showed a status.
      }
    };
    setStatusInfo({ payment_status: "checking" });
    poll();
    return () => {
      cancelledFlag = true;
    };
  }, [sessionId]);

  useEffect(() => {
    if (cancelled) toast.message("Checkout cancelled. No charges made.");
  }, [cancelled]);

  const startCheckout = async () => {
    setLoading(true);
    setCheckoutError("");
    try {
      // Backend expects Literal["donation", "license"]. The UI tab is "donate"
      // for shorter labels but the API kind must be "donation".
      const body = {
        kind: tab === "donate" ? "donation" : tab,
        donor_name: donor.donor_name || undefined,
        donor_email: donor.donor_email || undefined,
        organization: donor.organization || undefined,
        notes: donor.notes || undefined,
        origin_url: window.location.origin,
      };
      if (tab === "donate") {
        if (donateChoice === "custom") {
          const amt = parseFloat(customAmount);
          if (!amt || amt < 1) {
            toast.error("Enter at least $1 to donate.");
            setLoading(false);
            return;
          }
          body.custom_amount = amt;
        } else {
          body.package_id = donateChoice;
        }
      } else {
        body.package_id = licenseChoice;
      }
      const { data } = await api.post("/checkout/session", body);
      window.location.href = data.url;
    } catch (e) {
      const detail = e.response?.data?.detail || "Could not start checkout. Please try again.";
      // Persist the message inline so it survives the toast auto-dismiss —
      // this was the root of the "button does nothing" bug (503 error
      // was flashing away in 4 seconds and users never saw it).
      setCheckoutError(typeof detail === "string" ? detail : "Could not start checkout.");
      toast.error(typeof detail === "string" ? detail : "Could not start checkout.");
      setLoading(false);
    }
  };

  if (sessionId && statusInfo) {
    return <PaymentStatus info={statusInfo} onContinue={() => nav("/support", { replace: true })} />;
  }

  return (
    <div className="bg-tcpi-bg min-h-screen" data-testid="support-page">
      <Seo
        path="/support"
        title="Support TCPI — Donate or Partner"
        description="Help fund a participant's path. Every contribution supports facilitator hours, workbook materials, and reentry seats. Partner with us as an institution or give a one-time or recurring donation in any amount."
      />
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="text-center mb-12">
          <div className="overline mb-4">Support the Initiative</div>
          <h1 className="text-4xl md:text-6xl text-[#F7F5F0] leading-[1.1]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            Stand with the men <span className="italic text-[#C9A961]">earning their crowns.</span>
          </h1>
          <p className="text-[#D1CFC9] mt-6 max-w-2xl mx-auto leading-relaxed">
            Every dollar funds curriculum delivery, facilitator training, and reentry seats — work that
            interrupts cycles of incarceration one life at a time.
          </p>
          <p
            className="text-xs text-[#C9A961] mt-4 tracking-wide"
            data-testid="support-foundation-attribution"
          >
            The Crown Path Initiative is a program of <span className="font-semibold">The 1123 Foundation</span>.
            All donations are received by The 1123 Foundation.
          </p>
        </div>

        {/* Tab switcher */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex bg-[#121110] border border-[#C9A961]/20 rounded-full p-1" data-testid="support-tabs">
            <button
              onClick={() => setTab("donate")}
              className={`px-6 py-2.5 rounded-full text-sm tracking-wide transition flex items-center gap-2 ${
                tab === "donate" ? "bg-[#C9A961] text-[#0A0908]" : "text-[#D1CFC9] hover:text-[#C9A961]"
              }`}
              data-testid="tab-donate"
            >
              <Heart className="w-4 h-4" /> Donate
            </button>
            <button
              onClick={() => setTab("license")}
              className={`px-6 py-2.5 rounded-full text-sm tracking-wide transition flex items-center gap-2 ${
                tab === "license" ? "bg-[#C9A961] text-[#0A0908]" : "text-[#D1CFC9] hover:text-[#C9A961]"
              }`}
              data-testid="tab-license"
            >
              <Building2 className="w-4 h-4" /> Institutional Partnership
            </button>
          </div>
        </div>

        {/* Donation cards */}
        {tab === "donate" && packages && (
          <div className="grid lg:grid-cols-3 gap-6 mb-8" data-testid="donation-options">
            {packages.donations.map((p) => (
              <button
                key={p.id}
                onClick={() => setDonateChoice(p.id)}
                className={`card-bento text-left transition ${
                  donateChoice === p.id ? "border-[#C9A961] gold-glow" : ""
                }`}
                data-testid={`donate-${p.id}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="overline text-xs">{p.label}</span>
                  {donateChoice === p.id && <Check className="w-5 h-5 text-[#C9A961]" />}
                </div>
                <div className="text-5xl text-[#C9A961] mb-2" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                  ${p.amount.toFixed(0)}
                </div>
                <p className="text-sm text-[#D1CFC9]">
                  {p.id === "supporter" && "Sponsors one participant's workbook materials."}
                  {p.id === "advocate" && "Funds one week of facilitator-led group sessions."}
                  {p.id === "patron" && "Underwrites a participant's full 7-track journey."}
                </p>
              </button>
            ))}
            <button
              onClick={() => setDonateChoice("custom")}
              className={`card-bento text-left lg:col-span-3 transition ${
                donateChoice === "custom" ? "border-[#C9A961] gold-glow" : ""
              }`}
              data-testid="donate-custom"
            >
              <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
                <div className="flex-1">
                  <span className="overline text-xs">Custom amount</span>
                  <p className="text-sm text-[#D1CFC9] mt-2">
                    Give whatever you can. Every contribution moves the work forward.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-3xl text-[#C9A961]" style={{ fontFamily: "Cormorant Garamond, serif" }}>$</span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    placeholder="50"
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      setDonateChoice("custom");
                    }}
                    className="input-tcpi w-32 text-2xl"
                    data-testid="donate-custom-amount"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            </button>
          </div>
        )}

        {/* License cards */}
        {tab === "license" && packages && (
          <div className="grid md:grid-cols-3 gap-6 mb-8" data-testid="license-options">
            {packages.licenses.map((p) => (
              <button
                key={p.id}
                onClick={() => setLicenseChoice(p.id)}
                className={`card-bento text-left transition relative ${
                  licenseChoice === p.id ? "border-[#C9A961] gold-glow" : ""
                }`}
                data-testid={`license-${p.id}`}
              >
                {p.id === "cohort" && (
                  <span className="absolute -top-3 left-6 bg-[#C9A961] text-[#0A0908] text-[10px] uppercase tracking-widest px-3 py-1 rounded-full font-bold">
                    Most Common
                  </span>
                )}
                <div className="flex items-center justify-between mb-4">
                  <Crown className="w-6 h-6 text-[#C9A961]" strokeWidth={1.4} />
                  {licenseChoice === p.id && <Check className="w-5 h-5 text-[#C9A961]" />}
                </div>
                <div className="overline text-xs mb-2">{p.label}</div>
                <div className="text-5xl text-[#C9A961] mb-3" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                  ${p.amount.toLocaleString("en-US")}
                </div>
                <p className="text-sm text-[#D1CFC9] leading-relaxed">{p.description}</p>
              </button>
            ))}
          </div>
        )}

        {/* Donor / org form */}
        <div className="card-bento max-w-3xl mx-auto" data-testid="donor-form">
          <div className="overline mb-4">{tab === "donate" ? "Your details" : "Organization details"}</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[#D1CFC9] mb-2">
                {tab === "donate" ? "Your name" : "Contact name"}
              </label>
              <input
                className="input-tcpi"
                value={donor.donor_name}
                onChange={(e) => setDonor({ ...donor, donor_name: e.target.value })}
                data-testid="donor-name"
              />
            </div>
            <div>
              <label className="block text-sm text-[#D1CFC9] mb-2">Email</label>
              <input
                type="email"
                className="input-tcpi"
                value={donor.donor_email}
                onChange={(e) => setDonor({ ...donor, donor_email: e.target.value })}
                data-testid="donor-email"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm text-[#D1CFC9] mb-2">
                {tab === "donate" ? "Organization (optional)" : "Organization"}
              </label>
              <input
                className="input-tcpi"
                value={donor.organization}
                onChange={(e) => setDonor({ ...donor, organization: e.target.value })}
                data-testid="donor-org"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm text-[#D1CFC9] mb-2">Notes (optional)</label>
              <textarea
                rows={3}
                className="input-tcpi resize-none"
                value={donor.notes}
                onChange={(e) => setDonor({ ...donor, notes: e.target.value })}
                placeholder={tab === "donate" ? "In memory of, or any message you'd like to share." : "Tell us about your institution and rollout timeline."}
                data-testid="donor-notes"
              />
            </div>
          </div>

          {stripeMode === "unconfigured" && (
            <div
              className="mt-6 p-4 rounded-md border border-[#E58A8A]/40 bg-[#E58A8A]/5"
              data-testid="stripe-unconfigured-banner"
            >
              <div className="text-[#E58A8A] text-sm font-medium mb-1">
                Payments are temporarily unavailable
              </div>
              <p className="text-xs text-[#D1CFC9]">
                This deployment does not have a live Stripe key configured yet.
                Please check back shortly. If you are the site administrator: set
                <span className="font-mono text-[#C9A961]"> TCPI_STRIPE_RK_LIVE </span>
                in your Emergent production environment variables, then redeploy.
              </p>
            </div>
          )}

          <button
            onClick={startCheckout}
            disabled={loading || stripeMode === "unconfigured"}
            className="btn-primary w-full mt-6 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="checkout-btn"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Preparing secure checkout…</>
            ) : (
              <>Continue to secure payment <ArrowRight className="w-4 h-4" /></>
            )}
          </button>

          {checkoutError && (
            <div
              className="mt-3 p-3 rounded-md border border-[#E58A8A]/40 bg-[#E58A8A]/5 text-sm text-[#E58A8A]"
              data-testid="checkout-error-inline"
            >
              {checkoutError}
            </div>
          )}
          <p className="text-xs text-[#8A8782] mt-3 text-center" data-testid="stripe-mode-notice">
            {stripeMode === "test" && "Payments processed securely by Stripe. Test mode is currently active."}
            {stripeMode === "live" && "Payments processed securely by Stripe."}
            {stripeMode === "unconfigured" && (
              <span className="text-[#E58A8A]">Live payments not yet enabled on this deployment.</span>
            )}
            {stripeMode === null && "Payments processed securely by Stripe."}
          </p>
        </div>
      </section>
    </div>
  );
}

function PaymentStatus({ info, onContinue }) {
  const paid = info.payment_status === "paid";
  const failed = info.status === "expired" || info.payment_status === "failed";
  const pending = !paid && !failed;

  return (
    <div className="min-h-[80vh] flex items-center justify-center bg-tcpi-bg px-6" data-testid="payment-status">
      <div className="card-bento max-w-md w-full text-center grain relative">
        {pending && (
          <>
            <Loader2 className="w-12 h-12 text-[#C9A961] mx-auto mb-6 animate-spin" />
            <h2 className="text-3xl text-[#F7F5F0] mb-3" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              Confirming payment…
            </h2>
            <p className="text-[#8A8782] text-sm">This will only take a moment.</p>
          </>
        )}
        {paid && (
          <>
            <Sparkles className="w-12 h-12 text-[#C9A961] mx-auto mb-6" />
            <h2 className="text-3xl text-[#F7F5F0] mb-3" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              Thank you.
            </h2>
            <p className="text-[#D1CFC9] mb-4">
              Your{" "}
              <span className="text-[#C9A961]">${info.amount?.toFixed(2)}</span>{" "}
              {info.kind === "donation" ? "gift" : "partnership"} has been received.
            </p>
            <p className="text-sm text-[#8A8782] italic mb-8">
              "The crown is not given. It is earned." You are helping more men earn theirs.
            </p>
            <button onClick={onContinue} className="btn-primary" data-testid="status-continue-btn">
              Continue
            </button>
          </>
        )}
        {failed && (
          <>
            <h2 className="text-3xl text-[#F7F5F0] mb-3" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              Payment not completed
            </h2>
            <p className="text-[#D1CFC9] mb-6">Nothing was charged. You can try again whenever you're ready.</p>
            <button onClick={onContinue} className="btn-primary" data-testid="status-retry-btn">
              Back to Support
            </button>
          </>
        )}
      </div>
    </div>
  );
}
